import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  QueryState,
  Select,
  Skeleton,
  SlaBadge,
  Timeline,
  useToast,
} from '@yhelp/ui';
import type {
  Ticket,
  TicketAttachment,
  TicketClassification,
  TicketComment,
  TicketStatus,
} from '@yhelp/shared-types';
import { DashboardLayout } from '@/templates/DashboardLayout';
import { useAuth } from '@/auth';
import { useAuthorizedFetch } from '@/lib/useAuthorizedFetch';
import { FloatingTicketChat } from '@/components/organisms/FloatingTicketChat/FloatingTicketChat';
import { allowedNextStatuses, statusLabel } from '@/lib/ticket-status';
import { productLabel } from '@/lib/ticket-meta';
import { ClassificationBadge } from '@/components/molecules/ClassificationBadge';
import {
  TicketAttachmentField,
  uploadTicketAttachments,
  type PendingAttachment,
} from '@/components/molecules/TicketAttachmentField';

function slaStateFromUpdatedAt(updatedAt?: string): 'ok' | 'warning' | 'danger' {
  if (!updatedAt) return 'ok';
  const hours = (Date.now() - new Date(updatedAt).getTime()) / 3_600_000;
  if (hours > 48) return 'danger';
  if (hours > 24) return 'warning';
  return 'ok';
}

function DetailSkeleton() {
  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="space-y-4 lg:col-span-2">
        <Skeleton className="h-40 w-full rounded-xl" />
        <Card className="p-6">
          <Skeleton className="h-6 w-32" />
          <div className="mt-4 space-y-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        </Card>
      </div>
      <Skeleton className="h-64 w-full rounded-xl" />
    </div>
  );
}

export function TicketDetailPage() {
  const { ticketId } = useParams({ from: '/authenticated/tickets/$ticketId' });
  const { user } = useAuth();
  const authorizedFetch = useAuthorizedFetch();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [closeOpen, setCloseOpen] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);

  const ticket = useQuery({
    queryKey: ['ticket', ticketId],
    queryFn: () => authorizedFetch<{ data: Ticket }>(`/v1/tickets/${ticketId}`),
  });

  const comments = useQuery({
    queryKey: ['comments', ticketId],
    queryFn: () => authorizedFetch<{ data: TicketComment[] }>(`/v1/tickets/${ticketId}/comments`),
  });

  const attachments = useQuery({
    queryKey: ['attachments', ticketId],
    queryFn: () =>
      authorizedFetch<{ data: TicketAttachment[] }>(`/v1/tickets/${ticketId}/attachments`),
  });

  const uploadAttachments = useMutation({
    mutationFn: () => uploadTicketAttachments(ticketId, pendingAttachments, authorizedFetch),
    onSuccess: () => {
      setPendingAttachments([]);
      void attachments.refetch();
      toast({ variant: 'success', title: 'Anexos enviados' });
    },
    onError: () => toast({ variant: 'error', title: 'Erro ao enviar anexos' }),
  });

  const patchTicket = useMutation({
    mutationFn: (payload: { status?: TicketStatus; assignee_id?: string | null }) =>
      authorizedFetch(`/v1/tickets/${ticketId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
      void qc.invalidateQueries({ queryKey: ['tickets'] });
      toast({ variant: 'success', title: 'Chamado atualizado' });
    },
    onError: () => toast({ variant: 'error', title: 'Erro ao atualizar chamado' }),
  });

  const t = ticket.data?.data;
  const isAgent = user?.role !== 'requester' && user?.role !== 'viewer';
  const slaState = slaStateFromUpdatedAt(t?.updated_at);
  const nextStatuses = t?.status ? allowedNextStatuses(t.status) : [];
  const statusOptions = t?.status ? [t.status, ...nextStatuses.filter((s) => s !== t.status)] : [];
  const isLoading = ticket.isLoading || comments.isLoading;
  const isError = ticket.isError;

  const timelineItems =
    comments.data?.data.map((c) => ({
      id: c.id,
      authorName: c.author_name,
      body: c.body,
      timestamp: new Date(c.created_at).toLocaleString('pt-BR'),
      isInternal: Boolean(c.is_internal),
    })) ?? [];

  return (
    <DashboardLayout pageTitle={t?.title ?? 'Detalhe do chamado'}>
      {isLoading ? (
        <DetailSkeleton />
      ) : isError ? (
        <EmptyState
          variant="error"
          title="Erro ao carregar chamado"
          description="Não foi possível carregar os detalhes deste chamado."
          action={
            <Link to="/tickets">
              <Button variant="secondary">Voltar aos chamados</Button>
            </Link>
          }
        />
      ) : (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            <Card className="p-6">
              <h2 className="text-xl font-bold text-yhelp-black dark:text-white">{t?.title}</h2>
              <p className="mt-2 text-neutral-600 dark:text-neutral-300">{t?.description}</p>
            </Card>
            <Card className="p-6" data-tour="ticket-timeline">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-yhelp-black dark:text-white">Conversa</h3>
                <p className="text-xs text-muted">Use o chat flutuante para responder</p>
              </div>
              <QueryState
                isEmpty={!timelineItems.length}
                emptyProps={{
                  title: 'Nenhuma mensagem ainda',
                  description: 'Clique no botão amarelo no canto inferior esquerdo para enviar.',
                }}
                className="mt-4"
              >
                <Timeline items={timelineItems} className="mt-4" />
              </QueryState>
            </Card>
            <Card className="p-6" data-tour="ticket-attachments">
              <h3 className="font-bold text-yhelp-black dark:text-white">Anexos</h3>
              {attachments.data?.data.length ? (
                <ul className="mt-3 space-y-2">
                  {attachments.data.data.map((a) => (
                    <li
                      key={a.id}
                      className="flex items-center justify-between rounded-lg border border-neutral-200 px-3 py-2 text-sm dark:border-neutral-700"
                    >
                      <span className="truncate font-medium">{a.original_name}</span>
                      <span className="shrink-0 text-xs text-muted">
                        {(a.size_bytes / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-sm text-muted">Nenhum anexo ainda.</p>
              )}
              <div className="mt-4">
                <TicketAttachmentField
                  files={pendingAttachments}
                  onChange={setPendingAttachments}
                  disabled={uploadAttachments.isPending}
                />
                {pendingAttachments.length > 0 ? (
                  <Button
                    className="mt-3"
                    size="sm"
                    loading={uploadAttachments.isPending}
                    onClick={() => uploadAttachments.mutate()}
                  >
                    Enviar anexos
                  </Button>
                ) : null}
              </div>
            </Card>
          </div>
          <Card className="p-6 lg:col-span-1" data-tour="ticket-details-panel">
            <h3 className="font-bold text-yhelp-black dark:text-white">Detalhes</h3>
            <dl className="mt-4 space-y-3 text-sm">
              <div data-tour="ticket-status-field">
                <dt className="text-muted">Status</dt>
                <dd>
                  {isAgent ? (
                    <Select
                      className="mt-1"
                      value={t?.status ?? 'aberto'}
                      onChange={(e) =>
                        patchTicket.mutate({ status: e.target.value as TicketStatus })
                      }
                    >
                      {statusOptions.map((s) => (
                        <option key={s} value={s}>
                          {statusLabel(s)}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Badge variant="status" status={t?.status ?? 'aberto'} className="mt-1">
                      {statusLabel(String(t?.status ?? '—'))}
                    </Badge>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-muted">Produto</dt>
                <dd className="mt-1 text-sm font-medium text-yhelp-black dark:text-white">
                  {productLabel(t?.product)}
                </dd>
              </div>
              <div>
                <dt className="text-muted">Classificação</dt>
                <dd className="mt-1">
                  {t?.classification ? (
                    <ClassificationBadge
                      classification={t.classification as TicketClassification}
                    />
                  ) : (
                    '—'
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-muted">Prioridade</dt>
                <dd>
                  {t?.priority ? (
                    <Badge variant="priority" priority={t.priority} className="mt-1">
                      {t.priority}
                    </Badge>
                  ) : (
                    '—'
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-muted">SLA</dt>
                <dd className="mt-1">
                  <SlaBadge
                    state={slaState}
                    label={
                      slaState === 'ok'
                        ? 'no prazo'
                        : slaState === 'warning'
                          ? 'atenção'
                          : 'em risco'
                    }
                  />
                </dd>
              </div>
            </dl>
            {isAgent && t?.status !== 'fechado' && nextStatuses.includes('fechado') ? (
              <Button
                className="mt-4 w-full"
                variant="secondary"
                data-tour="ticket-close-btn"
                onClick={() => setCloseOpen(true)}
              >
                Fechar chamado
              </Button>
            ) : null}
          </Card>
        </div>
      )}
      <FloatingTicketChat ticketId={ticketId} isAgent={isAgent} />
      <Dialog
        open={closeOpen}
        variant="destructive"
        title="Fechar chamado?"
        description="O solicitante será notificado quando o status mudar para fechado."
        confirmLabel="Fechar"
        onConfirm={() => {
          patchTicket.mutate({ status: 'fechado' });
          setCloseOpen(false);
        }}
        onCancel={() => setCloseOpen(false)}
        loading={patchTicket.isPending}
      />
    </DashboardLayout>
  );
}
